import { INTERVAL, interval_record_names } from '@voladeum/client';
import { ChartDataset } from 'chart.js';
import { CandleData } from 'client/voladeum/candles/types';
import { Indicator } from 'client/voladeum/indicators/types';
import { SignalApi } from 'client/voladeum/signal/types';
import { FORMAT_TYPE } from 'cyprus/enums';
import { formatValue } from 'cyprus/formats';
import { INDICATOR_COLOR } from 'enums';
import { colors } from 'framework/theme/constants/colors';
import moment from 'moment';

import { BACKGROUND, BORDER, INDICATOR_COLOR_HEX } from '../constants';

export const getTimeseriesAxis = ({
  min,
  max,
  display,
}: {
  min?: number;
  max?: number;
  display: boolean;
}) => ({
  type: 'timeseries' as const,
  time: {
    unit: 'hour' as const,
    displayFormats: { hour: 'hh:mm A' },
  },
  display,
  grid: { display: true, drawOnChartArea: false },
  border: { display: false },
  ticks: {
    source: 'data' as const,
    maxTicksLimit: 12,
    padding: 4,
    font: { size: 12, family: 'Roboto' },
    color: colors.light[1],
    maxRotation: 0,
    callback: (value: any, index: number) =>
      index === 0 ? '' : moment.utc(value).format('hh:mm A'),
  },
  min,
  max,
});

export const getTimeAxis = ({ display }: { display: boolean }) => ({
  type: 'time' as const,
  display,
  offset: false,
  time: {
    unit: 'day' as const,
    displayFormats: { day: 'MMM D, YYYY' },
    tooltipFormat: 'MMM D, YYYY',
  },
  stacked: true,
  grid: { display: true, drawOnChartArea: false },
  border: { display: false },
  ticks: {
    source: 'data' as const,
    maxTicksLimit: 12,
    padding: 4,
    font: { size: 12, family: 'Roboto' },
    color: colors.light[1],
    maxRotation: 0,
    callback: (value: any, index: number) =>
      index === 0 ? '' : moment.utc(value).format('MMM D, YYYY'),
  },
});

export const getMinuteAxis = () => ({
  type: 'linear' as const,
  position: 'right' as const,
  bounds: 'data' as const,
  stacked: false,
  title: { display: false, text: 'Minutes', color: colors.light[1] },
  grid: { color: colors.dark[7] },
  border: { display: false },
  max: interval_record_names[INTERVAL.MINUTE],
  min: 0,
  ticks: {
    stepSize: 240,
    maxTicksLimit: 10,
    color: colors.light[1],
  },
});

export const getPriceAxis = ({ scale, buffer }: { scale: number; buffer: number }) => ({
  type: 'linear' as const,
  position: 'right' as const,
  title: { display: false, text: 'Price', color: colors.light[1] },
  grid: { color: colors.dark[7] },
  border: { display: false },
  weight: 8,
  ticks: {
    maxTicksLimit: 10,
    padding: 4,
    callback: function (value: any) {
      return formatValue(value, { format: FORMAT_TYPE.CURRENCY_SCALE, scale, decimal: 1 });
    },
    font: { size: 12, family: 'Roboto' },
    color: colors.light[1],
  },
  afterDataLimits: (scale: any) => {
    const range = scale.max - scale.min;
    scale.min = scale.min - range * buffer;
  },
});

export const getVolumeAxis = ({ multiplier }: { multiplier: number }) => ({
  display: false,
  type: 'linear' as const,
  position: 'left' as const,
  beginAtZero: true,
  title: { display: false, text: 'Volume', color: colors.light[1] },
  grid: { display: false, color: colors.dark[7] },
  border: { display: false },
  weight: 2,
  ticks: {
    padding: 4,
    maxTicksLimit: 4,
    callback: function (value: any) {
      return formatValue(value, { format: FORMAT_TYPE.NUMBER, decimal: 0 });
    },
    font: { size: 12, family: 'Roboto' },
    color: colors.light[1],
  },
  afterDataLimits: (scale: any) => {
    const range = scale.max - scale.min;
    scale.max = scale.max + range * multiplier;
  },
});

export const getOscillatorAxis = () => ({
  position: 'right' as const,
  min: 0,
  max: 100,
  title: { display: false, text: 'Oscillator', color: colors.light[1] },
  grid: { color: colors.dark[7] },
  border: { display: false },
  ticks: {
    padding: 4,
    maxTicksLimit: 3,
    callback: function (value: any) {
      return formatValue(value, { format: FORMAT_TYPE.NUMBER, decimal: 0 });
    },
    font: { size: 12, family: 'Roboto' },
    color: colors.light[1],
    stepSize: 1,
  },
});

export const getBarAxis = () => ({
  display: false,
  position: 'left' as const,
  title: { display: false, text: 'Bar', color: colors.light[1] },

  grid: { color: colors.dark[7] },
  border: { display: false },
  ticks: {
    padding: 4,
    maxTicksLimit: 10,
    callback: function (value: any) {
      return formatValue(value, { format: FORMAT_TYPE.NUMBER, decimal: 0 });
    },
    font: { size: 12, family: 'Roboto' },
    color: colors.light[1],
    stepSize: 1,
  },
});

export const getLineAxis = ({ min, max, buffer }: { min: number; max: number; buffer: number }) => {
  const range = max - min;
  const buffer_min = min - range * buffer;
  const buffer_max = max + range * buffer;

  return {
    position: 'right' as const,
    min: buffer_min,
    max: buffer_max,
    title: { display: false, text: 'Line', color: colors.light[1] },
    grid: { color: colors.dark[7] },
    border: { display: false },
    ticks: {
      padding: 4,
      maxTicksLimit: 10,
      stepSize: 1,
      callback: function (value: any) {
        return formatValue(value, { format: FORMAT_TYPE.NUMBER, decimal: 0 });
      },
      font: { size: 12, family: 'Roboto' },
      color: colors.light[1],
    },
  };
};

export const navigator_options = {
  responsive: true,
  maintainAspectRatio: false,

  scales: {
    x: { type: 'timeseries' as const, time: { unit: 'day' as const }, display: false },
    y: {
      position: 'right' as const,
      title: { display: false, text: 'Navigator', color: colors.light[1] },
      ticks: { padding: 4, display: false },
    },
  },
  plugins: { legend: { display: false }, tooltip: { enabled: false } },
  elements: { line: { tension: 0 } },
  hover: { mode: 'nearest' as const, intersect: false },
};

export const getCandlesData = ({ data }: { data: CandleData[] }) => ({
  type: 'candlestick' as const,
  label: 'OHLC',
  yAxisID: 'line',
  order: 3,
  data,
  borderColors: {
    up: BORDER.GREEN,
    down: BORDER.RED,
    unchanged: BORDER.GREY,
  },
  backgroundColors: {
    up: BACKGROUND.GREEN,
    down: BACKGROUND.RED,
    unchanged: BACKGROUND.GREY,
  },
});

export const getVolumeData = ({ data }: { data: CandleData[] }) => ({
  type: 'bar' as const,
  label: 'Volume',
  data: data.map((candle) => ({ x: candle.x, y: candle.v })),
  yAxisID: 'volume',
  backgroundColor: data.map((c) => (c.c >= c.o ? BACKGROUND.GREEN : BACKGROUND.RED)),
  borderColor: data.map((c) => (c.c >= c.o ? BORDER.GREEN : BORDER.RED)),
  borderWidth: 1,
  order: 2,
});

export const getNavigatorData = ({ data }: { data: CandleData[] }) => ({
  type: 'line' as const,
  label: 'Price',
  data: data.map((candle) => ({ x: candle.x, y: candle.c })),
  borderColor: BORDER.WHITE,
  borderWidth: 1,
  pointRadius: 0,
});

export const getLinesDataset = (
  candle: CandleData[],
  signal: SignalApi,
  indicator: Indicator
): ChartDataset<'line'>[] => {
  const { indicator_name, color } = indicator;
  const data = signal[indicator.identifier] || [[]];

  const color_hex = color as INDICATOR_COLOR;
  return data.map((row, idx) => {
    const dataset = row || [];
    return {
      type: 'line',
      label: data.length > 1 ? `${indicator_name} (part ${idx + 1})` : indicator_name,
      data: dataset.map((value, i) => ({ x: candle[i].x, y: value === null ? NaN : value })),
      borderColor: INDICATOR_COLOR_HEX[color_hex] || INDICATOR_COLOR_HEX[INDICATOR_COLOR.WHITE],
      order: 1,
      pointRadius: 0,
      borderWidth: 1,
      yAxisID: 'line',
    };
  });
};

export const getLineDataset = (
  candles: CandleData[],
  signal: SignalApi,
  indicator: Indicator
): ChartDataset<'line'>[] => {
  const data = signal[indicator.identifier] || [[]];
  return data.map((row) => {
    const dataset = row || [];
    return {
      type: 'line',
      label: 'Line',
      data: dataset.map((v, i) => ({ x: candles[i].x, y: v === null ? NaN : v })),
      borderColor: colors.purple[4],
      pointRadius: 0,
      borderWidth: 1,
      yAxisID: 'line',
    };
  });
};

export const getMACDDataset = (candle: CandleData[], signals: SignalApi, indicator: Indicator) => {
  const data = signals[indicator.identifier] || [];
  const macd = data[0] || [];
  const signal = data[1] || [];
  const histogram = data[2] || [];

  return [
    {
      type: 'line' as const,
      label: `MACD`,
      data: macd.map((value, i) => ({ x: candle[i].x, y: value === null ? NaN : value })),
      borderColor: BORDER.BLUE,
      pointRadius: 0,
      borderWidth: 1,
      yAxisID: 'line',
    },
    {
      type: 'line' as const,
      label: `Signal`,
      data: signal.map((value, i) => ({ x: candle[i].x, y: value === null ? NaN : value })),
      borderColor: BORDER.RED,
      pointRadius: 0,
      borderWidth: 1,
      yAxisID: 'line',
    },
    {
      type: 'bar' as const,
      label: `Histogram`,
      data: histogram.map((value, i) => ({ x: candle[i].x, y: value === null ? NaN : value })),
      backgroundColor: histogram.map((v) =>
        v === null ? BACKGROUND.GREY : v >= 0 ? BACKGROUND.GREEN : BACKGROUND.RED
      ),
      borderColor: histogram.map((v) =>
        v === null ? BORDER.GREY : v >= 0 ? BORDER.GREEN : BORDER.RED
      ),
      borderWidth: 1,
      yAxisID: 'bar',
    },
  ];
};

export const getOscillatorsDataset = (
  candles: CandleData[],
  signal: SignalApi,
  indicator: Indicator
): ChartDataset<'line'>[] => {
  const { indicator_name } = indicator;
  const data = signal[indicator.identifier] || [[]];
  return data.map((row, idx) => {
    const dataset = row || [];
    return {
      type: 'line',
      label: data.length > 1 ? `${indicator_name} (part ${idx + 1})` : indicator_name,
      data: dataset.map((value, i) => ({ x: candles[i].x, y: value || 0 })),
      borderColor: colors.purple[4],
      backgroundColor: `${colors.purple[4]}44`,
      pointRadius: 0,
      borderWidth: 1,
      yAxisID: 'oscillator',
    };
  });
};

export const getVolumeDataset = (
  candles: CandleData[],
  signal: SignalApi,
  indicator: Indicator
): ChartDataset<'line'>[] => {
  const { indicator_name } = indicator;
  const data = signal[indicator.identifier] || [[]];
  return data.map((row, idx) => {
    const dataset = row || [];
    return {
      type: 'line',
      label: data.length > 1 ? `${indicator_name} (part ${idx + 1})` : indicator_name,
      data: dataset.map((value, i) => ({ x: candles[i].x, y: value || 0 })),
      borderColor: colors.purple[4],
      backgroundColor: `${colors.purple[4]}44`,
      pointRadius: 0,
      borderWidth: 1,
      yAxisID: 'volume',
    };
  });
};
